import React from "react";
import Navbar from "../../assets/Navbar/Navbar";
import Footer from "../../assets/Footer/Footer";
import BreadcrumbHeader from "../../assets/Breadcrumbs/Breadcrumbs";
import Slideshow from "../../assets/Slideshow/Slideshow";
import "./PersonalProjects.css";

// Temporary placeholders – replace later with real PingPong Coach visuals
import pingpong1 from "../../assets/Images/pingpongcoach/1.png";
import pingpong2 from "../../assets/Images/pingpongcoach/2.png";
import pingpong3 from "../../assets/Images/pingpongcoach/3.jpeg";
import pingpong4 from "../../assets/Images/pingpongcoach/4.jpeg";
import pingpong5 from "../../assets/Images/pingpongcoach/5.mp4";
import pingpong6 from "../../assets/Images/pingpongcoach/6.mp4";

const PingPongCoach = () => {
  const images = [
    pingpong1,
    pingpong2,
    pingpong3,
    pingpong4,
    pingpong5,
    pingpong6,
  ];

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="page-container">
      <Navbar />

      <div className="content-lo1-headings">
        <h1>PingPong Coach App</h1>
        <p>
          A duo project created during Smart Mobile, focused on helping users
          improve their ping pong skills through structured practice,
          feedback, and repetition.
        </p>
      </div>

      <div className="line"></div>

      <div className="breadcrumb-wrap" style={{ padding: "0.1rem 7.5rem" }}>
        <BreadcrumbHeader />
      </div>

      <div className="content-lo1">
        <div className="pp-layout">
          {/* LEFT */}
          <div className="pp-left">
            {/* Anchors */}
            <div className="pp-anchors">
              <button onClick={() => scrollToId("overview")}>Overview</button>
              <button onClick={() => scrollToId("collaboration")}>
                Collaboration
              </button>
              <button onClick={() => scrollToId("process")}>
                Design Process
              </button>
              <button onClick={() => scrollToId("features")}>Key Features</button>
              <button onClick={() => scrollToId("learning")}>
                What I Learned
              </button>
            </div>

            {/* OVERVIEW */}
            <section id="overview" className="pp-section">
              <h2>Overview</h2>
              <p>
                PingPong Coach was developed as a duo case where the assignment
                was to design an app that helps users improve a skill.
                We chose ping pong because it naturally fits repetition,
                timing, and measurable progress.
              </p>
              <p>
                The goal wasn’t to create a complex sports app, but a focused
                coaching tool that helps users practice with intention instead
                of just playing randomly.
              </p>
            </section>

            {/* COLLABORATION */}
            <section id="collaboration" className="pp-section">
              <h2>Collaboration & My Role</h2>
              <p>
                This project was developed together with one teammate.
                We divided responsibilities based on our strengths.
              </p>
              <p>
                My main focus was on the <b>design side of the app</b>: shaping
                the user flow, creating prototypes, and making sure the
                interface stayed clear and consistent as the idea evolved.
              </p>
              <ul>
                <li>Creating flowcharts and early concept sketches</li>
                <li>Designing low- and mid-fidelity prototypes in Figma</li>
                <li>Developing UI structure for training and game features</li>
                <li>Supporting usability testing and design iteration</li>
              </ul>
              <p>
                While my teammate focused more on the technical implementation,
                I made sure the experience made sense from a user perspective.
              </p>
            </section>

            {/* PROCESS */}
            <section id="process" className="pp-section">
              <h2>Design Process</h2>
              <p>
                We started by defining what kind of problem we wanted to solve:
                how do you help someone improve a physical skill using an app?
              </p>
              <p>
                From there, I worked on visualizing ideas through sketches,
                flowcharts, and prototypes. Early versions were intentionally
                simple, so we could test structure and logic before polishing
                visuals.
              </p>
              <p>
                After conducting interviews and gathering feedback, I translated
                insights into personas and empathy maps. This helped refine the
                training flow and made sure features didn’t feel random or
                overwhelming.
              </p>
              <p>
                In the final phase, I focused on refining UI details for
                motion tracking and timing-based game elements, ensuring visual
                consistency while supporting the back-end work done by my
                teammate.
              </p>
            </section>

            {/* SNAPSHOT (TOOLS) */}
            <div className="pp-snapshot">
              <div className="pp-snapshot-item">
                <span className="pp-label">Project Type</span>
                <span className="pp-value">Duo Project / Smart Mobile</span>
              </div>

              <div className="pp-snapshot-item">
                <span className="pp-label">Goal</span>
                <span className="pp-value">
                  Help users improve ping pong skills through structured practice
                </span>
              </div>

              <div className="pp-snapshot-item">
                <span className="pp-label">Users</span>
                <span className="pp-value">
                  Beginner to intermediate ping pong players
                </span>
              </div>

              <div className="pp-snapshot-item">
                <span className="pp-label">My Role</span>
                <span className="pp-value">
                  UX/UI design, user flow, prototyping, usability iteration
                </span>
              </div>

              <div className="pp-snapshot-item">
                <span className="pp-label">Tools</span>
                <span className="pp-value">
                  Figma, Swift (iOS), Git
                </span>
              </div>
            </div>

            {/* FEATURES */}
            <section id="features" className="pp-section">
              <h2>Key Features</h2>
              <ul>
                <li>
                  <b>Guided training sessions</b> focused on specific ping pong skills
                </li>
                <li>
                  <b>Motion and timing-based exercises</b> to improve coordination
                </li>
                <li>
                  <b>Clear feedback moments</b> after each session
                </li>
                <li>
                  <b>Simple UI flow</b> designed for quick use during practice
                </li>
              </ul>
              <p>
                The emphasis was always on clarity and usability, not on adding
                features just for the sake of it.
              </p>
            </section>

            {/* LEARNING */}
            <section id="learning" className="pp-section">
              <h2>What I Learned</h2>
              <p>
                This project taught me how important structure and collaboration
                are when designing interactive products.
              </p>
              <ul>
                <li>How early sketches and flowcharts save time later</li>
                <li>How user research directly shapes better design decisions</li>
                <li>How to design UI that supports real-world physical activity</li>
                <li>How to collaborate with a developer and align design with logic</li>
              </ul>
            </section>
          </div>

          {/* RIGHT */}
          <aside className="pp-right">
            {images.length > 0 ? (
              <Slideshow images={images} />
            ) : (
              <div className="slideshow-placeholder">
                <p>Slideshow Placeholder</p>
                <p>Add images to the images array to display slideshow</p>
              </div>
            )}

            <div className="pp-caption">
              <p></p>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PingPongCoach;
